import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, KeyRound } from 'lucide-react';
import api from '../../services/api';
import usePackOffers from '../../hooks/usePackOffers';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php')
            .then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

/**
 * Modal d'achat de packs de codes catégorie (accès multi-sujet).
 * Offres configurables dans `/gestion/packs` (pack_offers.type='category_code').
 */
export default function CategoryCodeCheckoutModal({
    thematiqueId, categoryId, categoryLabel, label, mode = 'quiz',
    onClose, onSuccess,
}) {
    const { offers: PACKS_RAW } = usePackOffers('category_code');
    const PACKS = PACKS_RAW.map((o, idx) => ({
        type: o.slug.replace(/^cc-/, ''),
        nb: o.nb_codes,
        price: o.price_eur,
        offer_id: o.id,
        popular: idx === Math.floor(PACKS_RAW.length / 2),
    }));
    const [selectedPack, setSelectedPack] = useState(null);
    useEffect(() => {
        if (!selectedPack && PACKS.length > 0) {
            const popular = PACKS.find(p => p.popular) || PACKS[0];
            setSelectedPack(popular.type);
        }
    }, [PACKS, selectedPack]);
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');
    const [simulating, setSimulating] = useState(false);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleBuy = async (packType) => {
        setSelectedPack(packType);
        setError(null);
        setLoading(true);
        try {
            const { data } = await api.post('/category-code/packs.php', {
                user_thematique_id: thematiqueId,
                user_category_id: categoryId,
                pack_type: packType,
                label: label || undefined,
                mode: mode || 'quiz',
            });
            if (data.success) {
                if (data.data.simulated) {
                    setSimulating(true);
                    onSuccess?.();
                    setTimeout(() => onClose(), 1500);
                } else if (data.data.client_secret) {
                    setClientSecret(data.data.client_secret);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
        }
        setLoading(false);
    };

    const handleSimulate = async (packType) => {
        setSimulating(true);
        setError(null);
        try {
            const { data } = await api.post('/category-code/packs.php', {
                user_thematique_id: thematiqueId,
                user_category_id: categoryId,
                pack_type: packType,
                label: label || undefined,
                mode: mode || 'quiz',
                simulate: true,
            });
            if (data.data?.simulated) {
                onSuccess?.();
                setTimeout(() => onClose(), 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur');
            setSimulating(false);
        }
    };

    const pack = PACKS.find(p => p.type === selectedPack);

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal" onClick={e => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title">
                        {clientSecret
                            ? `Paiement — ${pack?.nb} codes panel (${pack?.price}\u20ac)`
                            : <><KeyRound size={18} /> Codes catégorie — {categoryLabel}</>
                        }
                    </span>
                    <button className="checkout-modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: '16px' }}>{error}</div>}

                    {simulating && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            <p><strong>Paiement simul&eacute;</strong> — Codes g&eacute;n&eacute;r&eacute;s !</p>
                        </div>
                    )}

                    {!clientSecret && !simulating && (
                        <div className="sub-modal-plans">
                            <div className="sub-modal-subtitle">
                                Codes catégorie pour &laquo;&nbsp;{categoryLabel}&nbsp;&raquo;
                                <br />
                                <span style={{ fontSize: '0.85em', color: 'var(--text-muted)' }}>
                                    1 code = 1 participant, acc&egrave;s &agrave; tous les sujets de la cat&eacute;gorie
                                </span>
                            </div>

                            <div className="share-panel__pack-grid">
                                {PACKS.map(p => (
                                    <div key={p.type}
                                        className={`share-panel__pack-card ${p.popular ? 'share-panel__pack-card--popular' : ''} ${selectedPack === p.type ? 'share-panel__pack-card--active' : ''}`}
                                        onClick={() => !loading && setSelectedPack(p.type)}
                                        style={{ cursor: loading ? 'default' : 'pointer' }}>
                                        {p.popular && <span className="share-panel__pack-badge">Populaire</span>}
                                        <KeyRound size={20} />
                                        <div className="share-panel__pack-nb">{p.nb} codes</div>
                                        <div className="share-panel__pack-price">{p.price}&euro;</div>
                                        <div className="share-panel__pack-unit">{(p.price / p.nb * 100).toFixed(0)}c/code</div>
                                    </div>
                                ))}
                            </div>

                            {label && (
                                <div style={{ textAlign: 'center', margin: '8px 0', fontSize: '0.85em', color: 'var(--text-muted)' }}>
                                    Groupe : <strong>{label}</strong>
                                </div>
                            )}

                            <button className="btn btn-primary sub-modal-cta"
                                onClick={() => selectedPack && handleBuy(selectedPack)}
                                disabled={!selectedPack || loading}>
                                {loading
                                    ? <><Loader2 size={16} className="spin" /> Chargement...</>
                                    : <><CreditCard size={16} /> Acheter {pack?.nb} codes</>
                                }
                            </button>
                            {isDev && selectedPack && (
                                <button className="btn btn-sm"
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer' }}
                                    onClick={() => handleSimulate(selectedPack)}>
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">Paiement unique. Codes g&eacute;n&eacute;r&eacute;s apr&egrave;s paiement. Les participants n'ont pas besoin de cr&eacute;er un compte.</p>
                        </div>
                    )}

                    {clientSecret && !simulating && (
                        <div>
                            <button className="sub-modal-back"
                                onClick={() => { setClientSecret(null); setSelectedPack('50'); setError(null); }}>
                                &larr; Changer de formule
                            </button>
                            <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
